/**
 * <ImportProgressBar /> — percent + ETA + smooth interpolation.
 *
 * Per BUILD_READINESS_M29.md acceptance criterion 13: shows
 * percentage + time remaining, smooth (Emil Kowalski) interpolation
 * so the bar glides between updates rather than jerking.
 *
 * Smooth interpolation: motion-safe CSS transition on the fill
 * width; framer-motion's `layout` would over-commit for a single
 * bar. CSS transition w/ token duration is enough.
 *
 * Hidden when `fraction` is null (the task isn't measurable — e.g.,
 * AI clarification is open-ended).
 */

export interface ImportProgressBarProps {
  /** 0 – 1. Set to null to hide the bar. */
  fraction: number | null;
  /** Estimated seconds remaining. Null = unknown. */
  etaSeconds?: number | null;
  /** Optional label above the bar. */
  label?: string;
}

export function ImportProgressBar({
  fraction,
  etaSeconds,
  label,
}: ImportProgressBarProps) {
  if (fraction === null || fraction === undefined) return null;

  const pct = Math.max(0, Math.min(1, fraction)) * 100;
  const pctRounded = Math.round(pct);

  return (
    <div
      className="space-y-1"
      role="progressbar"
      aria-valuenow={pctRounded}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label || "Import progress"}
    >
      {(label || etaSeconds != null) && (
        <div className="flex items-center justify-between text-[10px] text-text-tertiary">
          <span>{label ?? ""}</span>
          {etaSeconds != null && etaSeconds > 0 && (
            <span>≈ {_formatEta(etaSeconds)} remaining</span>
          )}
        </div>
      )}
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-sunken">
        <div
          className="h-full rounded-full bg-brand-blue transition-[width] duration-medium ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="text-[10px] tabular-nums text-text-tertiary">
        {pctRounded}%
      </div>
    </div>
  );
}

function _formatEta(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m} min`;
  const h = Math.round(m / 60);
  return `${h} hr`;
}
